import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/utils/api';

const iso = (d) => d.toISOString().slice(0, 10);

export default function DownloadMPS() {
  const now = new Date();
  const [fromDate, setFromDate] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [toDate, setToDate] = useState(iso(now));
  const [outlet, setOutlet] = useState('all');
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/cost-centres').then(r => setOutlets(r.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (fromDate > toDate) return toast.error('From date must be before To date');
    setLoading(true);
    try {
      const { data: { job_id } } = await api.post(`/download-mps/start?from_date=${fromDate}&to_date=${toDate}&outlet=${encodeURIComponent(outlet)}`);
      let status = 'running';
      for (let i = 0; i < 200 && status === 'running'; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const st = await api.get(`/download-mps/status/${job_id}`);
        status = st.data.status;
        if (status === 'error') throw new Error(st.data.error || 'Export failed');
      }
      if (status !== 'done') throw new Error('Export timed out');
      const res = await api.get(`/download-mps/file/${job_id}`, { responseType: 'blob', timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MPS_CEO_${fromDate}_to_${toDate}${outlet !== 'all' ? '_' + outlet : ''}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('MPS downloaded');
    } catch (e) {
      toast.error(e?.message || 'Download failed');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'block mt-1 text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:border-indigo-400';
  return (
    <div className="space-y-6" data-testid="download-mps-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Download MPS</h1>
        <p className="text-sm text-slate-500 mt-1">Excel with one sheet per CEO tab: Employees, Salary Approvals, Transfer Funds, Payments, ShiftPro Attendance, Approvals, Rosters and Employees Data.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase">From Date</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={inputCls} data-testid="download-mps-from-date" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase">To Date</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={inputCls} data-testid="download-mps-to-date" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase">Outlet</label>
          <select value={outlet} onChange={e => setOutlet(e.target.value)} className={inputCls} data-testid="download-mps-outlet">
            <option value="all">All Outlets</option>
            {outlets.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
          </select>
        </div>
        <Button onClick={submit} disabled={loading} data-testid="download-mps-submit">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          {loading ? 'Preparing...' : 'Submit & Download'}
        </Button>
      </div>
    </div>
  );
}
